import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { FUEL_GRADES } from '../constants';

ChartJS.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api';

const formatPrice = (value) => `£${Number(value).toFixed(3)}/L`;

export default function FuelDashboardPage() {
  const [fuelGrade, setFuelGrade] = useState('E10');
  const [days, setDays] = useState(365);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${backendUrl}/fuel/price-trend?fuel_grade=${encodeURIComponent(fuelGrade)}&days=${days}`
        );
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.detail || 'Failed to load fuel prices.');
        }
        const payload = await response.json();
        if (!ignore) setPoints(payload);
      } catch (err) {
        if (!ignore) setError(err.message || 'Failed to load fuel prices.');
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    load();
    return () => { ignore = true; };
  }, [fuelGrade, days]);

  const summary = useMemo(() => {
    if (!points.length) return null;
    const prices = points.map((point) => Number(point.price_per_unit));
    return {
      latest: prices[prices.length - 1],
      lowest: Math.min(...prices),
      average: prices.reduce((total, price) => total + price, 0) / prices.length,
    };
  }, [points]);

  const storeSpend = useMemo(() => {
    const totals = new Map();

    points.forEach((point) => {
      const store = point.store_name?.trim() || 'Unknown store';
      let amount = point.total_price == null ? Number.NaN : Number(point.total_price);

      if (!Number.isFinite(amount)) {
        amount = Number(point.litres) * Number(point.price_per_unit);
      }
      if (!Number.isFinite(amount)) return;

      totals.set(store, (totals.get(store) || 0) + amount);
    });

    return [...totals.entries()]
      .map(([store, amount]) => ({ store, amount }))
      .sort((a, b) => b.amount - a.amount);
  }, [points]);

  const chartData = {
    labels: points.map((point) => point.date),
    datasets: [{
      label: `${fuelGrade} price (£/L)`,
      data: points.map((point) => Number(point.price_per_unit)),
      borderColor: 'rgba(44, 143, 109, 1)',
      backgroundColor: 'rgba(44, 143, 109, 0.18)',
      pointRadius: 5,
      pointHoverRadius: 7,
      tension: 0.2,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: 'index' },
    scales: {
      y: {
        title: { display: true, text: 'Price per litre' },
        ticks: { callback: (value) => `£${Number(value).toFixed(3)}` },
      },
      x: { title: { display: true, text: 'Purchase date' } },
    },
    plugins: {
      tooltip: {
        callbacks: {
          afterLabel: (context) => {
            const point = points[context.dataIndex];
            const details = [];
            if (point.store_name) details.push(point.store_name);
            if (point.litres != null) details.push(`${Number(point.litres).toFixed(3)} L`);
            return details;
          },
        },
      },
    },
  };

  const spendChartData = {
    labels: storeSpend.map(({ store }) => store),
    datasets: [{
      label: 'Expenses by Store',
      data: storeSpend.map(({ amount }) => amount),
      backgroundColor: [
        'rgba(255, 99, 132, 0.7)',
        'rgba(54, 162, 235, 0.7)',
        'rgba(255, 206, 86, 0.7)',
        'rgba(75, 192, 192, 0.7)',
        'rgba(153, 102, 255, 0.7)',
        'rgba(255, 159, 64, 0.7)',
      ],
      borderColor: [
        'rgba(255, 99, 132, 1)',
        'rgba(54, 162, 235, 1)',
        'rgba(255, 206, 86, 1)',
        'rgba(75, 192, 192, 1)',
        'rgba(153, 102, 255, 1)',
        'rgba(255, 159, 64, 1)',
      ],
      borderWidth: 1,
    }],
  };

  const spendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
      },
      title: {
        display: true,
        text: 'Expenses by Store',
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.raw || 0;
            const allData = context.chart.data.datasets[0].data;
            const total = allData.reduce((sum, current) => sum + parseFloat(current), 0);
            const percentage = total > 0
              ? `${((parseFloat(value) / total) * 100).toFixed(2)}%`
              : '0%';
            return `${label}: £${parseFloat(value).toFixed(2)} (${percentage})`;
          },
        },
      },
    },
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Fuel Grade</InputLabel>
            <Select
              value={fuelGrade}
              label="Fuel Grade"
              onChange={(event) => setFuelGrade(event.target.value)}
            >
              {FUEL_GRADES.map((grade) => (
                <MenuItem key={grade.id} value={grade.id}>{grade.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Date Range</InputLabel>
            <Select value={days} label="Date Range" onChange={(event) => setDays(event.target.value)}>
              <MenuItem value={30}>Last 30 Days</MenuItem>
              <MenuItem value={90}>Last 90 Days</MenuItem>
              <MenuItem value={365}>Last Year</MenuItem>
              <MenuItem value={0}>All History</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && points.length === 0 && (
        <Alert severity="info">No {fuelGrade} purchases were found for this date range.</Alert>
      )}

      {!loading && !error && points.length > 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              ['Latest', summary.latest],
              ['Average', summary.average],
              ['Lowest', summary.lowest],
            ].map(([label, value]) => (
              <Grid item size={{ xs: 12, sm: 4 }} key={label}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                  <Typography variant="h6">{formatPrice(value)}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, lg: 7 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Price trend</Typography>
                <Box sx={{ height: { xs: 320, sm: 380 } }}>
                  <Line data={chartData} options={chartOptions} />
                </Box>
              </Paper>
            </Grid>
            <Grid item size={{ xs: 12, lg: 5 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Spend by store</Typography>
                <Box sx={{ height: '400px', p: 1 }}>
                  <Doughnut data={spendChartData} options={spendChartOptions} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
